import { useLocation, useNavigate } from 'react-router-dom';
import { Switch } from '@mantine/core';
import { TbPalette } from 'react-icons/tb';
import { FEATURE_FLAG_NAMES, useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { designCounterpart, setDesignPreference, useIsV2Surface } from '../../../utils/design';
import { useWorkflowChrome } from '../WorkflowChromeContext';
import { Row } from './style';

/**
 * The one way in and out of the redesign, in the profile menu.
 *
 * It was a pill in the top bar, and before that six different controls scattered across the pages
 * they switched. The menu is where it belongs now: what it flips stopped being a mode the page is
 * in and became a setting the person keeps, and this is the drawer the app puts settings in.
 *
 * Behind the `v2_ui` flag. It draws nothing where the page has no counterpart — an offer to cross
 * to a room that was never built is worse than no offer.
 */
export default function DesignSwitch({ closePopover }: { closePopover?: () => void }) {
  const isV2UI = useFeatureFlag(FEATURE_FLAG_NAMES.V2_UI);
  const location = useLocation();
  const navigate = useNavigate();
  const chrome = useWorkflowChrome();
  const inV2 = useIsV2Surface(location.pathname, location.search);

  if (!isV2UI) return null;

  /* The report cannot be answered for generically: its classic page lives at another address, with
     the regulator in the path, and it is the one page with unsaved edits to lose. It publishes both
     its way back and the question to ask first. */
  const override = (chrome as any)?.designSwitch;
  const counterpart = override ?? designCounterpart(location.pathname, location.search);

  if (!counterpart) return null;

  const flip = () => {
    if (typeof counterpart.confirm === 'function' && !counterpart.confirm()) return;

    setDesignPreference(inV2 ? 'classic' : 'v2');
    /* Most stages hold both designs at one address and simply redraw; this is for the report, and
       for dropping a `?design=` the address may still be pinned with. */
    if (counterpart.to !== `${location.pathname}${location.search}`) navigate(counterpart.to);
    closePopover?.();
  };

  return (
    <Row type="button" onClick={flip}>
      {/* Not a sparkle — in this app sparkles mean the model did something. Not panes either: the
          row above it is a list of blocks, and two block-shaped marks in a row of five read as one
          thing said twice. A palette is neither, and it is the word. */}
      <TbPalette size={16} color="#868E96" />
      <span className="label">New design</span>
      <Switch
        size="xs"
        checked={inV2}
        /* The row is the control; the switch is what it looks like. Two hit targets that do the
           same thing is one of them stealing the click. */
        onChange={() => {}}
        tabIndex={-1}
        aria-hidden
      />
    </Row>
  );
}
